import React, { useMemo } from 'react';
import { Polygon } from 'react-leaflet';
import type { GeoJSON as GeoJSONType } from '../../lib/types';

interface SpotlightMaskProps {
  polygon: GeoJSONType.Polygon;
  fillOpacity?: number;
  fillColor?: string;
}

// Leaflet [lat, lng] order (the <Polygon> component's native convention),
// not GeoJSON [lng, lat] — the supply-shed inner ring below is flipped from
// GeoJSON to match. Covers nearly the whole globe; poles are trimmed because
// Web Mercator distorts polygons into nonsense there.
const WORLD_OUTER_RING: [number, number][] = [
  [-85, -180],
  [-85, 180],
  [85, 180],
  [85, -180],
];

/**
 * Dims the basemap outside the supply shed by drawing a donut polygon:
 * outer ring = world, inner ring = supply shed. Only the supply shed remains
 * at full brightness. Intended for thematic basemaps (AGB, future
 * color-ramp rasters) where the shed needs to pop against a color-saturated
 * global backdrop.
 */
export const SpotlightMask: React.FC<SpotlightMaskProps> = ({
  polygon,
  fillOpacity = 0.55,
  fillColor = '#06100b',
}) => {
  const positions = useMemo<[number, number][][]>(() => {
    const outerRing = polygon.coordinates[0] ?? [];
    const innerRing = outerRing.map(([lng, lat]): [number, number] => [lat, lng]);
    return [WORLD_OUTER_RING, innerRing];
  }, [polygon]);

  return (
    <Polygon
      positions={positions}
      pathOptions={{
        fillColor,
        fillOpacity,
        stroke: false,
        interactive: false,
      }}
    />
  );
};
